import { supabase } from '../client/browser'
import type { SupabaseClient } from '@supabase/supabase-js'
import { v4 as uuidv4 } from 'uuid'

/**
 * Audit logging service
 */

interface AuditLogData {
  proId?: string
  userId?: string
  action: string
  resource: string
  resourceId?: string
  details?: any
  ipAddress?: string
  accessDenied?: boolean
  accessReason?: string
}

export const auditService = {
  /**
   * Create an audit log entry
   */
  async log(data: AuditLogData, client?: SupabaseClient) {
    try {
      // Use provided client or default to browser client
      const supabaseClient = client || supabase
      
      // Check if user is impersonating
      let impersonationInfo = null
      if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
        try {
          const actualUserStr = sessionStorage.getItem('actualUser')
          const impersonatedUserStr = sessionStorage.getItem('impersonatedUser')
          
          if (actualUserStr && impersonatedUserStr) {
            const actualUser = JSON.parse(actualUserStr)
            const impersonatedUser = JSON.parse(impersonatedUserStr)
            impersonationInfo = {
              actualUserId: actualUser.id,
              actualUserName: `${actualUser.firstName || ''} ${actualUser.lastName || ''}`.trim() || actualUser.email,
              impersonatedUserId: impersonatedUser.id,
              impersonatedUserName: `${impersonatedUser.firstName || ''} ${impersonatedUser.lastName || ''}`.trim() || impersonatedUser.email
            }
          }
        } catch (e) {
          console.error('[AuditService] Error checking impersonation state:', e)
        }
      }
      
      const generatedId = uuidv4()
      
      const insertData = {
        id: generatedId,
        ...data,
        ipAddress: data.ipAddress || (typeof window !== 'undefined' ? await getClientIp() : null),
        details: {
          ...data.details,
          ...(impersonationInfo && { impersonation: impersonationInfo })
        }
      }
      
      const { error } = await supabaseClient
        .from('audit_logs')
        .insert(insertData)

      if (error) {
        // Check if this is a foreign key constraint error for missing user
        if (error.code === '23503' && error.message.includes('users') && data.userId) {
          console.warn('[AuditService] User record not found in database for userId:', data.userId)
          console.warn('[AuditService] Skipping audit log entry. User needs to be created in users table.')
          // Don't throw error for missing user records - this can happen during signup
          return
        }

        // Check if this is a foreign key constraint error for missing therapist/pro
        if (error.code === '23503' && error.message.includes('pros') && data.proId) {
          console.warn('[AuditService] Pro record not found in database for proId:', data.proId)
          console.warn('[AuditService] Skipping audit log entry. Pro may have been deleted or not yet created.')
          // Don't throw error for missing pro records - this can happen during deletions or race conditions
          return
        }

        console.error('[AuditService] Failed to create audit log:', {
          error,
          errorMessage: error.message,
          errorCode: error.code,
          errorDetails: error.details,
          insertData
        })
        throw error // Re-throw to see in calling code
      }
    } catch (err) {
      // This is expected during build time or when client is not ready
      console.error('[AuditService] Error in log():', err)
      if (typeof window !== 'undefined') {
        console.error('[AuditService] Error logging audit:', err)
      }
    }
  },

  /**
   * Log a user action
   */
  async logUserAction(userId: string, action: string, resource: string, details?: any, client?: SupabaseClient) {
    return this.log({
      userId,
      action,
      resource,
      details
    }, client)
  },

  /**
   * Log a pro/therapist action
   */
  async logProAction(proId: string, action: string, resource: string, resourceId?: string, details?: any, client?: SupabaseClient) {
    return this.log({
      proId,
      action,
      resource,
      resourceId,
      details
    }, client)
  },

  /**
   * Log an access denial
   */
  async logAccessDenied(proId: string, resource: string, resourceId: string, reason: string, client?: SupabaseClient) {
    return this.log({
      proId,
      action: 'access_denied',
      resource,
      resourceId,
      accessDenied: true,
      accessReason: reason
    }, client)
  },

  /**
   * Log a bulk PHI read / export. Use at any handler that returns many
   * patient records — admin analytics dumps, CSV downloads, cron jobs
   * iterating PHI, therapist client-list loads. `count` is the number of
   * patient/session/note rows read; `filters` records the scope so the
   * read can be reproduced for an HIPAA access audit.
   *
   * Either `proId` or `userId` should identify the actor. When both are
   * absent, the row is still written with `action='phi_export'` so the
   * surface itself is at least visible.
   */
  async logExport(
    args: {
      resource: string
      count: number
      proId?: string
      userId?: string
      filters?: Record<string, unknown>
      details?: any
    },
    client?: SupabaseClient,
  ) {
    return this.log({
      proId: args.proId,
      userId: args.userId,
      action: 'phi_export',
      resource: args.resource,
      details: {
        count: args.count,
        filters: args.filters ?? {},
        ...args.details,
      },
    }, client)
  },

  /**
   * Log authentication events
   */
  async logAuth(action: 'login' | 'logout' | 'login_failed', details?: any, client?: SupabaseClient) {
    return this.log({
      action: `auth.${action}`,
      resource: 'auth',
      details
    }, client)
  },

  /**
   * Log successful login.
   *
   * `appName` identifies which surface the login was on (broker, client,
   * employee, employer, imwell, imwell-admin, imwell-clinician, therapist,
   * between-sessions). REQUIRED — Supabase Auth's built-in audit log can't
   * distinguish apps in a single-project setup, so this column is the only
   * way to answer "which app did the user sign into" for SOC2 evidence.
   */
  async logLogin(proId: string, email: string, appName: string, client?: SupabaseClient, ipAddress?: string) {
    console.log('[AuditService] logLogin called with:', { proId, email, appName, ipAddress })

    // Capture browser/device info for security tracking
    const deviceInfo = typeof window !== 'undefined' ? {
      userAgent: navigator.userAgent,
      platform: (navigator as any).userAgentData?.platform || navigator.userAgent,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookiesEnabled: navigator.cookieEnabled
    } : null

    return this.log({
      proId,
      action: 'auth.login',
      resource: 'auth',
      ipAddress,
      details: {
        email,
        portal: appName,
        timestamp: new Date().toISOString(),
        ...deviceInfo && { device: deviceInfo }
      }
    }, client)
  },

  /**
   * Log failed login attempt. `appName` follows the same convention as
   * `logLogin` — required so audit-log readers always know which surface
   * the failure happened on (key for SOC2 anomaly detection / per-app
   * lockout policies).
   */
  async logLoginFailed(email: string, reason: string, appName: string, client?: SupabaseClient, ipAddress?: string) {
    // Capture browser/device info for security tracking
    const deviceInfo = typeof window !== 'undefined' ? {
      userAgent: navigator.userAgent,
      platform: (navigator as any).userAgentData?.platform || navigator.userAgent,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookiesEnabled: navigator.cookieEnabled
    } : null

    return this.log({
      action: 'auth.login_failed',
      resource: 'auth',
      ipAddress,
      details: {
        email,
        portal: appName,
        reason,
        timestamp: new Date().toISOString(),
        ...deviceInfo && { device: deviceInfo }
      },
      accessDenied: true,
      accessReason: reason
    }, client)
  },

  /**
   * Log a password-reset send. Fired once per successful reset-email
   * dispatch (Supabase generateLink minted a recovery link + SendGrid
   * accepted the send). `appName` follows the same convention as
   * `logLogin`. Details include portal + email so SOC2 auditors can
   * reconstruct "who requested a password reset, when, from where."
   *
   * Not fired for unknown-email attempts (those return silent 200 to
   * prevent enumeration) or for rate-limited attempts.
   */
  async logResetRequested(email: string, appName: string, client?: SupabaseClient, ipAddress?: string) {
    return this.log({
      action: 'auth.reset_requested',
      resource: 'auth',
      ipAddress,
      details: {
        email,
        portal: appName,
        timestamp: new Date().toISOString(),
      },
    }, client)
  },

  /**
   * Log logout
   */
  async logLogout(proId: string, client?: SupabaseClient) {
    return this.log({
      proId,
      action: 'auth.logout',
      resource: 'auth',
      details: { timestamp: new Date().toISOString() }
    }, client)
  },

  /**
   * Get count of audit logs matching filters
   */
  async count(filters: {
    proId?: string
    userId?: string
    resource?: string
    resourceId?: string
    action?: string
    startDate?: Date
    endDate?: Date
  }, client?: SupabaseClient): Promise<number> {
    try {
      const supabaseClient = client || supabase
      
      let query = supabaseClient
        .from('audit_logs')
        .select('*', { count: 'exact', head: true })
      
      if (filters.proId) query = query.eq('proId', filters.proId)
      if (filters.userId) query = query.eq('userId', filters.userId)
      if (filters.resource) query = query.eq('resource', filters.resource)
      if (filters.resourceId) query = query.eq('resourceId', filters.resourceId)
      if (filters.action) query = query.eq('action', filters.action)
      if (filters.startDate) query = query.gte('createdAt', filters.startDate.toISOString())
      if (filters.endDate) query = query.lte('createdAt', filters.endDate.toISOString())
      
      const { count, error } = await query
      
      if (error) {
        console.error('[AuditService] Count error:', error)
        return 0
      }
      
      return count || 0
    } catch (err) {
      if (typeof window !== 'undefined') {
        console.error('[AuditService] Count error:', err)
      }
      return 0
    }
  },
  
  /**
   * Query audit logs
   */
  async query(filters: {
    proId?: string
    userId?: string
    resource?: string
    resourceId?: string
    action?: string
    startDate?: Date
    endDate?: Date
    limit?: number
    offset?: number
  }, client?: SupabaseClient) {
    try {
      // Use provided client or default to browser client
      const supabaseClient = client || supabase

      let query = supabaseClient
        .from('audit_logs')
        .select(`
          *,
          pro:pros!audit_logs_proId_fkey (
            id,
            firstName,
            lastName,
            email
          ),
          user:users!audit_logs_userId_fkey (
            id,
            firstName,
            lastName,
            email
          )
        `)
        .order('createdAt', { ascending: false })

      if (filters.proId) query = query.eq('proId', filters.proId)
      if (filters.userId) query = query.eq('userId', filters.userId)
      if (filters.resource) query = query.eq('resource', filters.resource)
      if (filters.resourceId) query = query.eq('resourceId', filters.resourceId)
      if (filters.action) query = query.eq('action', filters.action)
      if (filters.startDate) query = query.gte('createdAt', filters.startDate.toISOString())
      if (filters.endDate) query = query.lte('createdAt', filters.endDate.toISOString())
      
      // Apply pagination
      if (filters.limit) {
        query = query.limit(filters.limit)
      }
      if (filters.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1)
      }

      const { data, error } = await query

      if (error) {
        console.error('[AuditService] Query error:', error)
        return []
      }
      
      return data || []
    } catch (err) {
      // This is expected during build time or when client is not ready
      if (typeof window !== 'undefined') {
        console.error('[AuditService] Query error:', err)
      }
      return []
    }
  }
}

/**
 * Get client IP address (basic implementation)
 */
async function getClientIp(): Promise<string | null> {
  try {
    // This is a basic implementation - in production you might want to use
    // a more sophisticated approach or a service
    const response = await fetch('https://api.ipify.org?format=json')
    const data = await response.json()
    return data.ip
  } catch {
    return null
  }
}